// The two "Site defaults" a store sets in Buylist Config, Your buylist site:
// "Preselect a condition" and "Preselect cash or store credit for the customer".
//
// They are stored ONCE, per game, at
//   buylistConfig.config.gameSettings[categoryId].pricingOptions.defaultCondition
//   buylistConfig.config.gameSettings[categoryId].pricingOptions.defaultPayment
// and the kiosk already receives that block from GET /:storeName/buylist-config.
// This module only READS it. It must never grow a kiosk-side copy of either
// setting: a second place to hold one value is how the Sell To Us card came to
// ignore the store's choice in the first place (TKT-2026-106337).
//
// The rules mirror the buylist site's own resolver, sortswift-buylist
// src/utils/buylistDefaults.js (getStoreDefaultCondition and
// getStoreDefaultPayment), so a store's kiosk and its buylist site open a card
// the same way. Keep the two in step.

export const BUYLIST_CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DM'];
const FALLBACK_CONDITION = 'NM';

function pricingOptionsFor(gameSettings, categoryId) {
    if (!gameSettings || categoryId === undefined || categoryId === null) return {};
    // gameSettings is keyed by the category id as a string; product.categoryId
    // is a number. Property lookup coerces, so both spellings resolve.
    return gameSettings[categoryId]?.pricingOptions || {};
}

/**
 * The condition a Sell To Us card opens on for a product of this game.
 *
 * @param {object} gameSettings buylistConfig.config.gameSettings
 * @param {number|string} categoryId the product's game
 * @param {string[]} [conditions] the conditions the card offers
 * @returns {string}
 */
export function buylistDefaultCondition(gameSettings, categoryId, conditions = BUYLIST_CONDITIONS) {
    const configured = pricingOptionsFor(gameSettings, categoryId).defaultCondition;
    if (configured && conditions.includes(configured)) return configured;
    return conditions.includes(FALLBACK_CONDITION) ? FALLBACK_CONDITION : (conditions[0] || '');
}

/**
 * 'Cash' or 'Credit', the payment a Sell To Us card opens on for this game.
 * A game that pays only one way opens on that way whatever the preselect says.
 *
 * @param {object} gameSettings buylistConfig.config.gameSettings
 * @param {number|string} categoryId the product's game
 * @returns {'Cash'|'Credit'}
 */
export function buylistDefaultPayment(gameSettings, categoryId) {
    const options = pricingOptionsFor(gameSettings, categoryId);
    const enableCash = options.enableCash !== false;
    const enableCredit = options.enableCredit !== false;
    if (!enableCash && enableCredit) return 'Credit';
    if (enableCash && !enableCredit) return 'Cash';
    return String(options.defaultPayment || '').toLowerCase() === 'credit' ? 'Credit' : 'Cash';
}

/**
 * Whether a get-price response buys this item at `condition`, for either
 * payment. The printing fallback asks this of the condition the card opens
 * on, as the buylist site does; asking it of NM misjudged every printing for a
 * store that does not buy NM.
 *
 * @param {object|null} priceData one get-price response (all conditions)
 * @param {string} condition
 * @returns {boolean}
 */
export function buysAtCondition(priceData, condition) {
    if (!priceData || !condition) return false;
    return parseFloat(priceData[`${condition}FinalCash`]) > 0 ||
        parseFloat(priceData[`${condition}FinalCredit`]) > 0;
}
